import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { sanitizeHtml } from "@/lib/sanitize";
import { useAuth } from "@/context/AuthContext";

const ALLOWED_ROLES = ["administrator", "partner"];

function Badge({ ok, labelOk, labelBad, testid }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
        ok ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-red-50 text-red-700 ring-1 ring-red-200"
      }`}
    >
      <Icons.Circle className="h-2 w-2 fill-current" />
      {ok ? labelOk : labelBad}
    </span>
  );
}

function Panel({ title, icon, children, right, testid }) {
  const Icon = Icons[icon] || Icons.FileText;
  return (
    <section data-testid={testid} className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <div className="flex items-center gap-2 text-slate-800">
          <Icon className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-semibold">{title}</h3>
        </div>
        {right}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

const STATUS_STYLE = {
  canonical_active: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  canonical_shadow: "bg-slate-100 text-slate-600 ring-slate-200",
};
const TIER_STYLE = {
  "Core Operations": "text-emerald-700", "Core / Administrative": "text-sky-700",
  "Specialized Services": "text-violet-700", "Rare / Exceptional": "text-amber-700",
  "Future Product": "text-slate-500",
};

function GovernedLetterLibrary() {
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // {tid, name, loading, doc, error}

  useEffect(() => {
    api.get("/templates/letters/library")
      .then(({ data }) => setFamilies(data.families || []))
      .catch(() => setFamilies([])).finally(() => setLoading(false));
  }, []);

  const openPreview = async (f) => {
    setPreview({ tid: f.template_id, name: f.name, loading: true });
    try {
      const { data } = await api.get(`/templates/letters/library/${f.template_id}/preview`);
      if (data.blocked || data.error) setPreview({ tid: f.template_id, name: f.name, error: data.reason || data.error });
      else setPreview({ tid: f.template_id, name: f.name, doc: data.document, prov: data.provenance });
    } catch (e) {
      setPreview({ tid: f.template_id, name: f.name, error: "Preview failed" });
    }
  };

  if (loading) return <div className="py-10 text-center text-sm text-slate-400" data-testid="letter-library-loading">Loading library…</div>;

  return (
    <div className="space-y-4" data-testid="governed-letter-library">
      <p className="text-sm text-slate-500">
        Read-only. <span className="font-mono">canonical_shadow</span> families are certified assets awaiting a
        business trigger (V3-LETTER-R16); delivery triggers are decided separately after activation (V3-LETTER-R17).
        Preview renders sample output only — it never generates a document, sends, or creates provenance.
      </p>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm" data-testid="letter-library-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-4 py-2.5">Template</th><th className="px-3 py-2.5">Tier</th>
              <th className="px-3 py-2.5">Status</th><th className="px-3 py-2.5">Protected Clause</th>
              <th className="px-3 py-2.5">Trigger</th><th className="px-3 py-2.5">Delivery</th>
              <th className="px-3 py-2.5">Ver.</th><th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {families.map((f) => (
              <tr key={f.template_id} data-testid={`letter-library-row-${f.template_id}`} className="hover:bg-slate-50">
                <td className="px-4 py-2.5">
                  <div className="font-medium text-slate-800">{f.name}</div>
                  <div className="font-mono text-[11px] text-slate-400">{f.template_id}{f.variants?.length ? ` · ${f.variants.length} variants` : ""}</div>
                </td>
                <td className={`px-3 py-2.5 text-xs font-medium ${TIER_STYLE[f.tier] || "text-slate-500"}`}>{f.tier}</td>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${STATUS_STYLE[f.status] || "bg-slate-100 text-slate-600 ring-slate-200"}`}>
                    {f.status === "canonical_active" ? "Active" : "Shadow"}
                  </span>
                </td>
                <td className="px-3 py-2.5 font-mono text-[11px] text-slate-500">{f.protected_clause || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-slate-400">{f.business_trigger || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-slate-400">{f.delivery_model || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-slate-500">v{f.last_certified_version}</td>
                <td className="px-3 py-2.5">
                  <button data-testid={`letter-library-preview-${f.template_id}`} onClick={() => openPreview(f)}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-100">
                    <Icons.Eye className="h-3.5 w-3.5" />Preview
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="letter-preview-modal"
          onClick={() => setPreview(null)}>
          <div className="max-h-[85vh] w-full max-w-3xl overflow-hidden rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
              <div>
                <div className="text-sm font-semibold text-slate-800">{preview.name} — Sample Preview</div>
                <div className="text-[11px] font-mono text-slate-400">{preview.tid}</div>
              </div>
              <button onClick={() => setPreview(null)} data-testid="letter-preview-close" className="text-slate-400 hover:text-slate-700"><Icons.X className="h-4 w-4" /></button>
            </div>
            <div className="border-b border-amber-100 bg-amber-50 px-5 py-2 text-[11px] text-amber-700">
              PREVIEW ONLY · rendered with sample data · not generated, not sent, no provenance created.
            </div>
            <div className="max-h-[65vh] overflow-y-auto p-5">
              {preview.loading ? <div className="py-8 text-center text-sm text-slate-400">Rendering…</div>
                : preview.error ? <div className="py-8 text-center text-sm text-rose-500">{preview.error}</div>
                : <div className="prose prose-sm max-w-none" data-testid="letter-preview-body"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(preview.doc?.body_html || preview.doc?.body || "<em>No preview</em>") }} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LetterCertification() {
  const { user } = useAuth();
  const authorized = ALLOWED_ROLES.includes(user?.role);

  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [view, setView] = useState("certification");

  const loadHistory = useCallback(() => {
    api.get("/templates/letter-certification/history").then(({ data }) => setHistory(data.runs || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (authorized) loadHistory();
  }, [authorized, loadHistory]);

  const downloadBlob = (payload, mime, filename) => {
    const blob = new Blob([payload], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };
  const downloadB64Pdf = (b64, filename) => {
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    downloadBlob(bytes, "application/pdf", filename);
  };

  const run = useCallback(async () => {
    setLoading(true);
    setError("");
    setData(null);
    setSelected(null);
    try {
      const res = await api.post("/templates/letter-certification/run", { include_pdf: true });
      if (res.data.error) setError(res.data.error);
      else {
        setData(res.data);
        const withPdf = (res.data.results || []).find((r) => r.production_pdf_base64);
        setSelected(withPdf || (res.data.results || []).find((r) => r.in_family) || null);
        loadHistory();
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [loadHistory]);

  if (!authorized) {
    return (
      <div data-testid="letter-cert-unauthorized" className="mx-auto max-w-lg p-10 text-center">
        <Icons.ShieldAlert className="mx-auto mb-4 h-10 w-10 text-amber-500" />
        <h2 className="text-lg font-semibold text-slate-800">Restricted — Partner / Administrator only</h2>
        <p className="mt-2 text-sm text-slate-500">Legal-review certification tooling is not available for your role.</p>
      </div>
    );
  }

  const summary = data?.summary;
  const sem = selected?.layers?.semantic;
  const diffSet = new Set((sem?.diff || []).map((d) => d.text));

  return (
    <div data-testid="letter-certification-page" className="mx-auto max-w-6xl space-y-6 p-6">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <Icons.ScrollText className="h-5 w-5 text-indigo-600" />
          <h1 className="text-xl font-bold text-slate-900">Engagement Letter — Golden Master Family Certification</h1>
        </div>
        <p className="text-sm text-slate-500">
          Read-only exhaustive parity of the canonical family <span className="font-mono">TPL-LETTER-001</span>{" "}
          (Letter Wave A.2-R) vs the live production engagement templates. No editing, sending, promotion, or
          production changes are possible here.
        </p>
      </header>

      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="letter-gov-tabs">
        {[["certification", "Golden Master Certification"], ["library", "Governed Letter Library"]].map(([v, l]) => (
          <button key={v} data-testid={`letter-gov-tab-${v}`} onClick={() => setView(v)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
              view === v ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {l}
          </button>
        ))}
      </div>

      {view === "library" && <GovernedLetterLibrary />}
      {view === "certification" && (<>

      <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <button
          data-testid="letter-cert-run-button"
          onClick={run}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {loading ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Play className="h-4 w-4" />}
          Run exhaustive certification
        </button>
        {summary && (
          <>
            <Badge testid="letter-cert-overall-badge" ok={summary.family_certified} labelOk="FAMILY CERTIFIED" labelBad="NOT CERTIFIED" />
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-mono text-slate-600">
              {summary.certified_count}/{summary.standard_count} standard · {summary.outlier_count} out-of-family · status: {summary.template_status}
            </span>
            <button
              data-testid="letter-cert-download-report"
              onClick={() => {
                const clean = { ...data, results: data.results.map(({ production_pdf_base64, canonical_pdf_base64, ...r }) => r) };
                downloadBlob(JSON.stringify(clean, null, 2), "application/json", "engagement-letter-family-parity-report.json");
              }}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              <Icons.FileJson className="h-3.5 w-3.5" /> Parity report
            </button>
          </>
        )}
      </div>

      {error && (
        <div data-testid="letter-cert-error" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-red-200">
          {error}
        </div>
      )}

      {data && (
        <Panel title="Certification matrix" icon="Grid3x3" testid="letter-cert-matrix">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-slate-500">
                <tr className="border-b border-slate-100">
                  <th className="py-2 pr-3">Template</th>
                  <th className="py-2 pr-3">Family</th>
                  <th className="py-2 pr-3">Semantic</th>
                  <th className="py-2 pr-3">§6 Terms</th>
                  <th className="py-2 pr-3">Visual</th>
                  <th className="py-2 pr-3">Result</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((r) => (
                  <tr
                    key={r.template_db_id}
                    data-testid={`letter-cert-row-${r.template_db_id}`}
                    onClick={() => r.in_family && setSelected(r)}
                    className={`border-b border-slate-50 ${r.in_family ? "cursor-pointer hover:bg-slate-50" : "opacity-60"} ${
                      selected?.template_db_id === r.template_db_id ? "bg-indigo-50" : ""
                    }`}
                  >
                    <td className="py-1.5 pr-3 font-medium text-slate-700">{r.template_name}</td>
                    <td className="py-1.5 pr-3">{r.in_family ? "standard" : "out-of-family"}</td>
                    <td className="py-1.5 pr-3">{r.in_family ? (r.layers.semantic.pass ? "✓" : "✗") : "—"}</td>
                    <td className="py-1.5 pr-3">{r.in_family ? (r.layers.protected_components.pass ? "✓" : "✗") : "—"}</td>
                    <td className="py-1.5 pr-3">{r.in_family ? (r.layers.visual.pass ? "✓" : "✗") : "—"}</td>
                    <td className="py-1.5 pr-3">
                      {r.in_family ? (
                        <span className={r.certified ? "text-emerald-600 font-semibold" : "text-red-600 font-semibold"}>
                          {r.certified ? "CERTIFIED" : "FAILED"}
                        </span>
                      ) : (
                        <span className="text-slate-400">legacy path</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {selected?.in_family && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <span className="text-sm font-semibold text-slate-700">{selected.template_name}</span>
            <Badge ok={selected.certified} labelOk="CERTIFIED" labelBad="FAILED" />
            <span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-mono text-slate-500">
              src {selected.source_body_fingerprint}
            </span>
            <div className="ml-auto flex gap-2">
              {selected.production_pdf_base64 && (
                <button
                  data-testid="letter-cert-download-production"
                  onClick={() => downloadB64Pdf(selected.production_pdf_base64, `${selected.template_name}-production.pdf`)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Icons.Download className="h-3.5 w-3.5" /> Production PDF
                </button>
              )}
              {selected.canonical_pdf_base64 && (
                <button
                  data-testid="letter-cert-download-canonical"
                  onClick={() => downloadB64Pdf(selected.canonical_pdf_base64, `${selected.template_name}-canonical.pdf`)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Icons.Download className="h-3.5 w-3.5" /> Canonical PDF
                </button>
              )}
            </div>
          </div>

          <Panel title="Semantic content parity" icon="AlignLeft" testid="letter-cert-semantic"
            right={<Badge ok={sem?.pass} labelOk="EXACT" labelBad="DIFFERS" />}>
            <div className="flex flex-col gap-4 md:flex-row">
              {[["Production", sem?.production_lines, "prod"], ["Canonical", sem?.canonical_lines, "canon"]].map(
                ([title, lines, side]) => (
                  <div key={side} className="flex-1">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
                    <ol className="space-y-0.5 rounded-lg bg-slate-50 p-3 font-mono text-[11px] leading-relaxed text-slate-700">
                      {(lines || []).map((ln, i) => (
                        <li key={i} className={`whitespace-pre-wrap rounded px-1 ${diffSet.has(ln) ? (side === "prod" ? "bg-red-100" : "bg-emerald-100") : ""}`}>
                          {ln}
                        </li>
                      ))}
                    </ol>
                  </div>
                )
              )}
            </div>
          </Panel>

          <Panel title="Protected §6 Terms & Limitations" icon="Lock" testid="letter-cert-protected"
            right={<Badge ok={selected.layers.protected_components.pass} labelOk="MATCH" labelBad="MISMATCH" />}>
            <div className="space-y-1 text-sm text-slate-600">
              <div><span className="font-semibold">Component:</span> <span className="font-mono">{selected.layers.protected_components.component_id}</span></div>
              <div><span className="font-semibold">Fingerprint:</span> <span className="font-mono text-xs">{selected.layers.protected_components.fingerprint}</span></div>
            </div>
          </Panel>

          <Panel title="Governed sections + fingerprints" icon="ListTree" testid="letter-cert-sections">
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {selected.layers.structural.sections.map((s) => (
                <div key={s.section_id} className="rounded-lg border border-slate-200 p-3">
                  <div className="font-mono text-xs text-indigo-600">{s.section_id}</div>
                  <div className="text-sm font-medium text-slate-700">{s.heading || "—"}</div>
                  <div className="mt-1 font-mono text-[9px] text-slate-400">
                    {selected.layers.structural.section_fingerprints?.[s.section_id]}
                  </div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Visual / structural PDF" icon="FileText" testid="letter-cert-visual"
            right={<Badge ok={selected.layers.visual.pass} labelOk="ALIGNED" labelBad="MISMATCH" />}>
            <p className="mb-3 text-xs text-slate-500">
              Identical renderer ({selected.layers.visual.renderer}). Production {selected.layers.visual.production_pages}p · Canonical {selected.layers.visual.canonical_pages}p.
            </p>
            {selected.production_pdf_base64 && (
              <div className="flex flex-col gap-4 md:flex-row">
                {["production", "canonical"].map((side) => (
                  <div key={side} className="flex-1">
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{side}</p>
                    <iframe
                      data-testid={`letter-cert-pdf-${side}`}
                      title={`${side}-pdf`}
                      src={`data:application/pdf;base64,${selected[`${side}_pdf_base64`]}`}
                      className="h-[420px] w-full rounded-lg border border-slate-200"
                    />
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel title="Provenance" icon="Fingerprint" testid="letter-cert-provenance">
            <pre className="overflow-x-auto rounded-lg bg-slate-900 p-4 text-[11px] leading-relaxed text-slate-100">
              {JSON.stringify(selected.provenance, null, 2)}
            </pre>
          </Panel>
        </div>
      )}

      {history.length > 0 && (
        <Panel title="Certification run history (audit)" icon="History" testid="letter-cert-history">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-slate-500">
                <tr className="border-b border-slate-100">
                  <th className="py-2 pr-3">Executed at</th>
                  <th className="py-2 pr-3">By</th>
                  <th className="py-2 pr-3">Family</th>
                  <th className="py-2 pr-3">Result</th>
                  <th className="py-2 pr-3">Hash</th>
                  <th className="py-2 pr-3">Tool</th>
                </tr>
              </thead>
              <tbody className="font-mono text-slate-600">
                {history.map((h) => (
                  <tr key={h.id} className="border-b border-slate-50">
                    <td className="py-1.5 pr-3">{new Date(h.executed_at).toLocaleString()}</td>
                    <td className="py-1.5 pr-3">{h.executed_by}</td>
                    <td className="py-1.5 pr-3">{h.certified_count}/{h.family_size}</td>
                    <td className="py-1.5 pr-3">
                      <span className={h.family_certified ? "text-emerald-600" : "text-red-600"}>
                        {h.family_certified ? "CERTIFIED" : "FAILED"}
                      </span>
                    </td>
                    <td className="py-1.5 pr-3">{h.certification_hash}</td>
                    <td className="py-1.5 pr-3">{h.tool_version}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
      </>)}
    </div>
  );
}
